'use client'

import { useState, forwardRef, ReactNode } from 'react'
import {
  Calendar,
  Clock,
  MapPin,
  User,
  MessageSquare,
  Check,
  X,
  Loader2
} from 'lucide-react'
import { cn } from '../utils/styles'

export interface EventInvitationData {
  id: string
  status: 'pending' | 'accepted' | 'declined' | 'expired'
  invitedAt: string
  expiresAt: string
  message?: string
  event: {
    title: string
    description?: string
    type: string
    date: string
    time?: string
    location?: string
  }
  invitedBy: {
    name: string
  }
}

export interface EventInvitationCardProps {
  invitation: EventInvitationData
  onAccept?: (invitationId: string) => void | Promise<void>
  onDecline?: (invitationId: string, reason?: string) => void | Promise<void>
  loading?: boolean
  acceptLoading?: boolean
  declineLoading?: boolean
  disabled?: boolean
  showActions?: boolean
  formatDate?: (date: string) => string
  formatDateTime?: (date: string) => string
  className?: string
  acceptText?: string
  declineText?: string
  cancelText?: string
  confirmDeclineText?: string
  acceptingText?: string
  decliningText?: string
  declineReasonLabel?: string
  declineReasonPlaceholder?: string
  expiredText?: string
  pendingText?: string
  acceptedText?: string
  declinedText?: string
  invitedByText?: string
  personalMessageText?: string
  expiresOnText?: string
  declineReasonOptionalText?: string
  hideMessage?: boolean
  hideExpiry?: boolean
  hideInvitedBy?: boolean
  customStatusBadge?: (status: string, isExpired: boolean) => ReactNode
  customActions?: (invitation: EventInvitationData, isExpired: boolean, isPending: boolean) => ReactNode
}

export const EventInvitationCard = forwardRef<HTMLDivElement, EventInvitationCardProps>(({
  invitation,
  onAccept,
  onDecline,
  loading = false,
  acceptLoading = false,
  declineLoading = false,
  disabled = false,
  showActions = true,
  formatDate = (date: string) => new Date(date).toLocaleDateString(),
  formatDateTime = (date: string) => new Date(date).toLocaleString(),
  className,
  acceptText = "Accept Invitation",
  declineText = "Decline",
  cancelText = "Cancel",
  confirmDeclineText = "Confirm Decline",
  acceptingText = "Accepting...",
  decliningText = "Declining...",
  declineReasonLabel = "Reason for declining (optional)",
  declineReasonPlaceholder = "Let us know why you can't participate...",
  expiredText = "Expired",
  pendingText = "Pending",
  acceptedText = "Accepted",
  declinedText = "Declined",
  invitedByText = "Invited by",
  personalMessageText = "Personal Message",
  expiresOnText = "Expires on",
  declineReasonOptionalText = "(optional)",
  hideMessage = false,
  hideExpiry = false,
  hideInvitedBy = false,
  customStatusBadge,
  customActions,
  ...props
}, ref) => {
  const [declineReason, setDeclineReason] = useState('')
  const [showDeclineReason, setShowDeclineReason] = useState(false)

  const isExpired = new Date(invitation.expiresAt) < new Date()
  const isPending = invitation.status === 'pending' && !isExpired

  const handleAccept = async () => {
    if (disabled || loading || acceptLoading) return

    try {
      await onAccept?.(invitation.id)
    } catch (error) {
      console.error('Error accepting invitation:', error)
    }
  }

  const handleDecline = async () => {
    if (disabled || loading || declineLoading) return

    if (!showDeclineReason) {
      setShowDeclineReason(true)
      return
    }

    try {
      await onDecline?.(invitation.id, declineReason || undefined)
      setShowDeclineReason(false)
      setDeclineReason('')
    } catch (error) {
      console.error('Error declining invitation:', error)
    }
  }

  const handleCancelDecline = () => {
    setShowDeclineReason(false)
    setDeclineReason('')
  }

  const renderStatusBadge = () => {
    if (customStatusBadge) {
      return customStatusBadge(invitation.status, isExpired)
    }

    const getStatusClasses = () => {
      switch (invitation.status) {
        case 'accepted':
          return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-300 dark:border-green-700'
        case 'declined':
          return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900 dark:text-red-300 dark:border-red-700'
        case 'expired':
        case 'pending':
          if (isExpired) {
            return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600'
          }
          return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-300 dark:border-yellow-700'
        default:
          return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600'
      }
    }

    const getStatusText = () => {
      switch (invitation.status) {
        case 'accepted':
          return acceptedText
        case 'declined':
          return declinedText
        case 'expired':
        case 'pending':
          if (isExpired) return expiredText
          return pendingText
        default:
          return invitation.status
      }
    }

    return (
      <span className={cn(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border',
        getStatusClasses()
      )}>
        {getStatusText()}
      </span>
    )
  }

  const renderActions = () => {
    if (customActions) {
      return customActions(invitation, isExpired, isPending)
    }

    if (!showActions || !isPending) return null

    return (
      <div className="flex flex-col gap-3 pt-4 border-t">
        {showDeclineReason && (
          <div className="space-y-2">
            <label
              htmlFor="decline-reason"
              className="text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {declineReasonLabel}
            </label>
            <textarea
              id="decline-reason"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder={declineReasonPlaceholder}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
              disabled={disabled || loading}
            />
          </div>
        )}

        <div className="flex gap-2">
          {!showDeclineReason ? (
            <>
              <button
                onClick={handleAccept}
                disabled={disabled || loading || acceptLoading}
                className={cn(
                  'flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-colors',
                  'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {acceptLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {acceptingText}
                  </>
                ) : (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    {acceptText}
                  </>
                )}
              </button>
              <button
                onClick={handleDecline}
                disabled={disabled || loading || declineLoading}
                className={cn(
                  'flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-colors',
                  'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                  'dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                <X className="mr-2 h-4 w-4" />
                {declineText}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleCancelDecline}
                disabled={disabled || loading || declineLoading}
                className={cn(
                  'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                  'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                  'dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {cancelText}
              </button>
              <button
                onClick={handleDecline}
                disabled={disabled || loading || declineLoading}
                className={cn(
                  'flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-colors',
                  'bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {declineLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {decliningText}
                  </>
                ) : (
                  <>
                    <X className="mr-2 h-4 w-4" />
                    {confirmDeclineText}
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={ref}
      className={cn(
        'rounded-lg border bg-white shadow-sm dark:bg-gray-900 dark:border-gray-700',
        isPending && 'ring-2 ring-blue-500/50',
        className
      )}
      {...props}
    >
      {/* Header */}
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
              <Calendar className="h-5 w-5" />
              {invitation.event.title}
            </h3>
            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
              <span className={cn(
                'inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border capitalize',
                'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600'
              )}>
                {invitation.event.type}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {formatDateTime(
                  `${invitation.event.date}${invitation.event.time ? `T${invitation.event.time}` : ''}`
                )}
              </span>
              {invitation.event.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {invitation.event.location}
                </span>
              )}
            </div>
          </div>
          {renderStatusBadge()}
        </div>

        {invitation.event.description && (
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {invitation.event.description}
          </p>
        )}
      </div>

      {/* Content */}
      <div className="px-6 pb-6">
        <div className="space-y-4">
          {!hideInvitedBy && (
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <User className="h-4 w-4" />
              {invitedByText} {invitation.invitedBy.name} on {formatDate(invitation.invitedAt)}
            </div>
          )}

          {!hideMessage && invitation.message && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                <MessageSquare className="h-4 w-4" />
                {personalMessageText}
              </div>
              <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                {invitation.message}
              </div>
            </div>
          )}

          {!hideExpiry && !isExpired && invitation.status === 'pending' && (
            <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
              <Clock className="h-4 w-4" />
              {expiresOnText} {formatDateTime(invitation.expiresAt)}
            </div>
          )}
        </div>

        {renderActions()}
      </div>
    </div>
  )
})

EventInvitationCard.displayName = "EventInvitationCard"